// qs-useEventBus.ts
import type { EventBusEvents } from '@/types/qs-type-events'

class EventBus<T extends Record<string, any>> {
  private readonly listeners: Map<keyof T, Set<(data: any) => void>> = new Map()

  on<K extends keyof T>(event: K, callback: (data: T[K]) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback as (data: any) => void)
  }

  off<K extends keyof T>(event: K, callback?: (data: T[K]) => void): void {
    const eventListeners = this.listeners.get(event)
    if (!eventListeners) return

    if (callback) {
      eventListeners.delete(callback as (data: any) => void)
    } else {
      eventListeners.clear()
    }
  }

  once<K extends keyof T>(event: K, callback: (data: T[K]) => void): void {
    const onceCallback = (data: T[K]) => {
      callback(data)
      this.off(event, onceCallback as (data: T[K]) => void)
    }
    this.on(event, onceCallback)
  }

  emit<K extends keyof T>(event: K, data: T[K]): void {
    const eventListeners = this.listeners.get(event)
    if (!eventListeners) return

    eventListeners.forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error(`Error in event listener for ${String(event)}:`, error)
      }
    })
  }

  clear(): void {
    this.listeners.clear()
  }
}

export const eventBus = new EventBus<EventBusEvents>()

export function useEventBus() {
  return {
    emit: eventBus.emit.bind(eventBus),
    on: eventBus.on.bind(eventBus),
    off: eventBus.off.bind(eventBus),
    once: eventBus.once.bind(eventBus),
    clear: eventBus.clear.bind(eventBus)
  }
}

export const emitDrawingCompleted = (data: EventBusEvents['drawing-completed']): void => {
  eventBus.emit('drawing-completed', data)
}

export const emitLayerSelected = (data: EventBusEvents['layer-selected']): void => {
  eventBus.emit('layer-selected', data)
}

export const emitLayersChanged = (data: EventBusEvents['layers-changed']): void => {
  eventBus.emit('layers-changed', data)
}

export const emitDeleteModeChanged = (data: EventBusEvents['delete-mode-changed']): void => {
  eventBus.emit('delete-mode-changed', data)
}

export const emitPanelContentChanged = (data: EventBusEvents['panel-content-changed']): void => {
  eventBus.emit('panel-content-changed', data)
}

export const emitPanelHover = (data: EventBusEvents['panel-hover']): void => {
  eventBus.emit('panel-hover', data)
}

export const emitToolStateRefresh = (data: EventBusEvents['tool-state-refresh']): void => {
  eventBus.emit('tool-state-refresh', data)
}

export const emitPerformanceUpdate = (data: EventBusEvents['performance-update']): void => {
  eventBus.emit('performance-update', data)
}

export const emitForceCanvasReinit = (): void => {
  eventBus.emit('force-canvas-reinit', {})
}

export const emitResetBrushSizes = (): void => {
  eventBus.emit('reset-brush-sizes', undefined)
}